/**
 * Roteamento por papel cross-domínio (Fase 3): cada painel (admin/comerciante/
 * staff) vive num subdomínio próprio. `decidePanelAccess` é puro — o `PanelGuard`
 * o consome e só faz o `window.location.replace` como efeito. Admin é livre nos
 * três (D12). Sem papel de painel (customer/courier/Google sem convite) → deny
 * (nunca redirect → sem loops). Localhost → deny (dev local é por-porta, o
 * redirect cross-porta não vale o acoplamento).
 *
 * Construído e testado aqui; adoção pelos apps é Fase 3.
 */
export type PanelRole = 'admin' | 'staff' | 'merchant';

const SUBDOMAIN_BY_ROLE: Record<PanelRole, string> = {
  admin: 'admin',
  staff: 'staff',
  merchant: 'comerciante',
};

const HOME_PRIORITY: PanelRole[] = ['admin', 'staff', 'merchant'];

export const homeRole = (roles: string[]): PanelRole | null =>
  HOME_PRIORITY.find((r) => roles.includes(r)) ?? null;

/** Só o que `homeOrigin` lê de `window.location` — testes passam um literal. */
export type PanelLocation = Pick<Location, 'hostname' | 'protocol'>;

/**
 * Origin do painel-home do papel, derivado do host ATUAL trocando o 1º label
 * (staff.dev.favelafood.net -> comerciante.dev.favelafood.net). Funciona em dev
 * e prod sem env extra. Localhost/127.0.0.1 → null (dev local é por-porta).
 */
export function homeOrigin(role: PanelRole, loc: PanelLocation): string | null {
  if (loc.hostname === 'localhost' || loc.hostname === '127.0.0.1') return null;
  const base = loc.hostname.split('.').slice(1).join('.'); // "dev.favelafood.net"
  return `${loc.protocol}//${SUBDOMAIN_BY_ROLE[role]}.${base}`;
}

export type PanelDecision =
  | { kind: 'allow' }
  | { kind: 'redirect'; to: string }
  | { kind: 'deny' };

export function decidePanelAccess(
  allowed: PanelRole[],
  roles: string[],
  loc: PanelLocation,
): PanelDecision {
  if (roles.includes('admin')) return { kind: 'allow' }; // admin: livre nos 3 (D12)
  if (allowed.some((r) => roles.includes(r))) return { kind: 'allow' };
  const home = homeRole(roles);
  if (!home) return { kind: 'deny' }; // customer/courier/Google sem convite
  const to = homeOrigin(home, loc);
  return to ? { kind: 'redirect', to } : { kind: 'deny' };
}
